import { type Attribution, AttributionParam } from '../chromatic-lib/constants/AttributionParam';
import * as cookie from './cookie';
import { getSessionId } from './withSessionId';

const queryParams = [
  AttributionParam.utm_source,
  AttributionParam.utm_medium,
  AttributionParam.utm_campaign,
  AttributionParam.utm_content,
  AttributionParam.utm_term,
  AttributionParam.fbclid,
  AttributionParam.ad_id,
  AttributionParam.gclid,
  AttributionParam.gc_id,
] as const;

export const isNull = (value: unknown): value is null => Object.is(value, null);
export const isUndefined = (value: unknown): value is undefined => value === undefined;
export const isNil = <T>(value: T | undefined | null): value is undefined | null =>
  isNull(value) || isUndefined(value);
const isNotNil = <T>(value: T | undefined | null): value is T => !isNil(value);

const cookiePrefix = 'chromatic_internal';
export const cookieExpirationDays = 365;

export const campaignAttribution = (
  {
    sessionId,
    location,
    referrer,
    getCookie,
    setCookie,
  }: {
    sessionId: string;
    location: Pick<Location, 'href' | 'search'>;
    referrer: string;
    getCookie: (key: string) => string | null;
    setCookie: (key: string, value: string, days?: number) => void;
  } = {
    sessionId: getSessionId(),
    location: window.location,
    referrer: document.referrer,
    getCookie: cookie.getCookie,
    setCookie: cookie.setCookie,
  }
) => {
  const firstTouchKey = `${cookiePrefix}_first_touch`;
  const lastTouchKey = `${cookiePrefix}_last_touch`;
  const gcIdKey = `${cookiePrefix}_gc_id`;
  const fbclidKey = `${cookiePrefix}_fbclid`;

  const getFirstTouch = (): Attribution => {
    const f = getCookie(firstTouchKey);
    const object = f ? JSON.parse(f) : {};
    return object;
  };

  const getLastTouch = (): Attribution => {
    const l = getCookie(lastTouchKey);
    const object = l ? JSON.parse(l) : {};
    return object;
  };

  const getGcId = (): string | undefined => {
    const value = getCookie(gcIdKey);
    return value ?? undefined;
  };

  const getCurrentAttribution = (): Attribution => {
    // Get the query params from the current URL
    const sp = new URLSearchParams(location.search);

    const params = Object.fromEntries(
      queryParams
        .map((param) => {
          const value = sp.get(param);
          return value ? [param, value] : undefined;
        })
        .filter(isNotNil)
    );

    return {
      session_id: sessionId,
      ...params,
      referrer,
      landing_page: location.href,
    };
  };

  const storeAttributionValues = () => {
    const val = getCurrentAttribution();
    if (Object.keys(val).length === 0) {
      return;
    }

    const hasFirstTouch = getCookie(firstTouchKey);
    if (!hasFirstTouch) {
      console.debug('storing first touch', val);
      setCookie(firstTouchKey, JSON.stringify(val), cookieExpirationDays);
    }

    if (getLastTouch().session_id !== sessionId) {
      console.debug('storing last touch', val);
      setCookie(lastTouchKey, JSON.stringify(val), cookieExpirationDays);

      // Currently these two cookies are just set for debugging purposes
      if (val.fbclid) {
        setCookie(fbclidKey, val.fbclid);
      }

      if (val.gclid) {
        setCookie(gcIdKey, val.gclid);
      }
    }
  };

  return {
    firstTouchKey,
    lastTouchKey,
    getLastTouch,
    getFirstTouch,
    getCurrentAttribution,
    storeAttributionValues,
    getGcId,
  };
};
